import { useState, useMemo, useEffect, useRef } from "react";
import { Form, Button, Spinner } from "react-bootstrap";
import { useVirtualizer } from "@tanstack/react-virtual";
import { ICheckBoxList } from "./Interfaces";
import { ComponentStruct } from "../../common/Interfaces";
import "./css/CheckBoxList.css";

export default function CheckBoxList({
  options,
  onChange,
  onChecked,
  selected,
  archivedCheckbox = false,
  checked = false,
  selectAll = false,
  threeCharSearch = false,
  loading = false,
}: ICheckBoxList) {
  const [searchTerm, setSearchTerm] = useState("");
  const [uncontrolledSelectedItems, setUncontrolledSelectedItems] = useState<ComponentStruct[]>(
    selected ?? (selectAll ? options : [])
  );
  const [checkValue, setCheckValue] = useState(checked);
  const hide: boolean = process.env.REACT_APP_HIDE === "true";
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  // When `selected` is passed, the parent owns the selection state.
  const isSelectionControlled = selected !== undefined;
  // In uncontrolled mode, the component keeps its own local selection state.
  const selectedItems = isSelectionControlled
    ? selected
    : uncontrolledSelectedItems;

  useEffect(() => {
    // Keep select-all behavior for uncontrolled consumers when options load/change.
    if (!isSelectionControlled && selectAll) {
      setUncontrolledSelectedItems(options);
    }
  }, [isSelectionControlled, options, selectAll]);


  const filteredOptions = useMemo(
    () =>
      (threeCharSearch && searchTerm.length >= 3) || !threeCharSearch
        ? options.filter((option) => {
          if (option.label == null) return false;
          return option.label.toLowerCase().startsWith(searchTerm.toLowerCase());
        })
        : options,
    [options, searchTerm]
  );

  // O(1) checked lookup — rebuilds only when selectedItems changes
  const selectedKeys = useMemo(
    () => new Set(selectedItems.map((item) => item.key)),
    [selectedItems]
  );

  const virtualizer = useVirtualizer({
    count: filteredOptions.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => 24,
    overscan: 10,
  });

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleCheckboxChange = (item: ComponentStruct) => {
    const newSelectedItems = selectedKeys.has(item.key)
      ? selectedItems.filter((selectedItem) => selectedItem.key !== item.key)
      : [...selectedItems, item];

    // Controlled mode delegates persistence to the parent via onChange.
    if (!isSelectionControlled) {
      setUncontrolledSelectedItems(newSelectedItems);
    }
    onChange?.(newSelectedItems);
  };

  const handleSelectAll = () => {
    const merged = [...selectedItems];
    // Merge only currently filtered items that are not already selected.
    filteredOptions.forEach((opt) => {
      if (!selectedKeys.has(opt.key)) merged.push(opt);
    });

    if (!isSelectionControlled) {
      setUncontrolledSelectedItems(merged);
    }
    onChange?.(merged);
  };

  const handleDeselectAll = () => {
    const deselectedKeys = new Set(filteredOptions.map((o) => o.key));
    const newSelectedItems = selectedItems.filter(
      (item) => !deselectedKeys.has(item.key)
    );

    if (!isSelectionControlled) {
      setUncontrolledSelectedItems(newSelectedItems);
    }
    onChange?.(newSelectedItems);
  };
  return (
    <div>
      <div ref={scrollContainerRef} className="custom-container minimal-width">
            {loading ? (
              <div className="d-flex align-items-center justify-content-center h-100">
                <Spinner animation="border" size="sm" />
              </div>
            ) : (
            <>
            <div className="fixed-search">
              <input
                id="search-term"
                type="text"
                value={searchTerm}
                onChange={handleSearch}
                placeholder="Search..."
                className="form-control search-input"
              />
            </div>
            <div style={{ height: `${virtualizer.getTotalSize()}px`, position: "relative", paddingBottom: "5px" }}>
              {virtualizer.getVirtualItems().map((virtualItem) => {
                const option = filteredOptions[virtualItem.index];
                return (
                  <div
                    key={option.key}
                    style={{
                      position: "absolute",
                      top: virtualItem.start,
                      left: 0,
                      width: "100%",
                      height: `${virtualItem.size}px`,
                    }}
                  >
                    <Form className="custom-model-checkbox">
                      <Form.Check
                        id={option.key}
                        type="checkbox"
                        label={option.label}
                        checked={selectedKeys.has(option.key)}
                        onChange={() => handleCheckboxChange(option)}
                        className="ms-3"
                      />
                    </Form>
                  </div>
                );
              })}
            </div>
            </>
            )}
          </div>
      <div className="d-flex align-items-start pt-2">
          <Button size="sm" onClick={handleSelectAll}>
            Select All
          </Button>
          <Button className="ms-2" size="sm" onClick={handleDeselectAll}>
            Deselect All
          </Button>
          {hide ||
            (archivedCheckbox && (
              <Form className="custom-checkbox ms-4">
                <Form.Check
                  id={"achived"}
                  type="checkbox"
                  label={"Also show archived"}
                  className="small-label"
                  checked={checkValue}
                  onChange={(e) => {
                    setCheckValue(e.target.checked);
                    onChecked?.(e.target.checked);
                  }}
                />
              </Form>
            ))}
        </div>
    </div>
  );
}
